import type { PendingChatSubmission } from '../models/pending-chat-submission';
import type {
  ChatSurface,
  ChatEntrypoint,
} from '@cotera/client/app/etc/metrics/coco-events';

/** Minimal seed shape shared by PendingChatSubmission and DeferredChatSeedContext. */
type SeedArtifacts = {
  initialArtifacts?: { artifactType: string }[];
  initialActions?: { type: string }[];
};

/**
 * The agent/workflow surface a chat is *seeded* with, or `null` if the seed
 * carries neither. This is authoritative even before the seed is applied to the
 * conversation: agent/workflow nav uses `seedOnly`, so the artifacts and
 * focus_agent action stay deferred until the first message — the loaded
 * conversation looks empty (scratch) until then.
 */
export const deriveSeedArtifactSurface = (
  seed: SeedArtifacts | null
): 'agent' | 'workflow' | null => {
  const artifactTypes = (seed?.initialArtifacts ?? []).map(
    (a) => a.artifactType
  );
  const hasFocusAgent = (seed?.initialActions ?? []).some(
    (a) => a.type === 'focus_agent'
  );
  if (artifactTypes.includes('agent') || hasFocusAgent) {
    return 'agent';
  }
  if (artifactTypes.includes('workflow')) {
    return 'workflow';
  }
  return null;
};

/**
 * Classify a chat's {@link ChatSurface} from its seed (URL / pending
 * submission), for the freshly-seeded case where the persisted conversation
 * hasn't loaded yet. Returns `null` only for an existing chat with no seed —
 * the surface then comes from the loaded conversation via `useChatSurface`.
 */
export const deriveSeedChatSurface = (
  pending: PendingChatSubmission | null,
  isNewChat: boolean
): ChatSurface | null => {
  const artifactSurface = deriveSeedArtifactSurface(pending);
  if (artifactSurface !== null) {
    return artifactSurface;
  }
  if (isNewChat || pending !== null) {
    return 'scratch';
  }
  return null;
};

/**
 * Derive how the user arrived at a chat, as a fallback when the navigation
 * source didn't pass an explicit entrypoint in nav state.
 *
 * Explicit entrypoints (`sidebar`, `command_palette`, `shared_link`) are passed
 * through `location.state` by the navigating component and take precedence over
 * this — they can't be inferred from the chat itself.
 *
 * `isDirectLoad` should be true when the router navigation type is `POP` (a
 * fresh page load / refresh / back-forward with no in-app link click), which we
 * treat as a deep link.
 */
export const deriveChatEntrypoint = (
  pending: PendingChatSubmission | null,
  isNewChat: boolean,
  isDirectLoad: boolean = false
): ChatEntrypoint => {
  const artifactTypes = (pending?.initialArtifacts ?? []).map(
    (a) => a.artifactType
  );
  const hasFocusAgent = (pending?.initialActions ?? []).some(
    (a) => a.type === 'focus_agent'
  );
  if (artifactTypes.includes('agent') || hasFocusAgent) {
    return 'from_agent_nav';
  }
  if (artifactTypes.includes('workflow')) {
    return 'from_workflow_nav';
  }
  if (isNewChat) {
    return 'new';
  }
  if (isDirectLoad) {
    return 'deep_link';
  }
  return 'from_history';
};
